import type { PersonalPolicyPush } from "./features"

import { api } from "@/lib/api-client"

/** The three personal sources, by the name Hello AI uses for them. */
export type PersonalSpacesSource = "onedrive" | "mail" | "chats"

export type PersonalSpacesSkipReason =
  | "unsupported_type"
  | "oversize"
  | "labelled_or_encrypted"
  | "chat_linked_file"
  | "meeting_chat"
  | "not_owned"
  | "inline_or_small_image"
  | "storage_cap"
  | "other"

/** A lifecycle status; a status this build does not know arrives as "other". */
export type PersonalSpacesStatus =
  | "pending"
  | "active"
  | "paused_reconnect"
  | "hidden"
  | "purge_awaiting_approval"
  | "purge_deferred_stale"
  | "purge_suspended"
  | "purge_blocked_hold"
  | "purging"
  | "purged"
  | "other"

/** One user and one source. Counts, ids and enums only; there is no content field (PS-15). */
export type PersonalSpacesRow = {
  user_id: string
  display_name: string
  source: PersonalSpacesSource
  status: PersonalSpacesStatus
  reconnect_required: boolean
  purge_due_at: string | null
  items: number
  storage_bytes: number
  derived_bytes: number
  index_seconds: number
  freshness_p95_seconds: number | null
  freshness_alert: boolean
  skips_by_reason: Partial<Record<PersonalSpacesSkipReason, number>>
  skipped_total: number
}

export type PersonalSpacesSourceTotals = {
  users: number
  items: number
  storage_bytes: number
  derived_bytes: number
  index_seconds: number
  skips_by_reason: Partial<Record<PersonalSpacesSkipReason, number>>
  reconnect_required: number
  freshness_alerts: number
}

export type PersonalSpacesSummary = {
  /** False when personal sources are off, unwired, or this FILTH does not serve them. */
  available: boolean
  rows: PersonalSpacesRow[]
  totals: Partial<Record<PersonalSpacesSource, PersonalSpacesSourceTotals>>
  dropped_rows: number
  truncated: boolean
}

const base = "/admin/personal-spaces"

export async function getPersonalSpacesSummary(
  source?: PersonalSpacesSource
): Promise<PersonalSpacesSummary> {
  const response = await api.get(`${base}/summary`, { params: source ? { source } : undefined })
  return (response.data || response) as PersonalSpacesSummary
}

/** The same numbers as a CSV file (UTF-8, with a byte-order mark for spreadsheets). */
export async function downloadPersonalSpacesCsv(source?: PersonalSpacesSource): Promise<Blob> {
  const response = await api.get(`${base}/export.csv`, {
    params: source ? { source } : undefined,
    responseType: "blob",
  })
  if (response instanceof Blob) return response
  const raw = response as { data?: unknown }
  if (raw.data instanceof Blob) return raw.data
  throw new Error("Unexpected response from the personal sources export")
}

/** The state of a held mass-purge batch; a state this build does not know arrives as "other". */
export type PersonalSpacesBatchState = "awaiting_approval" | "approved" | "rejected" | "other"

/**
 * A mass-purge batch the guard is holding for a human decision. Counts only: no user
 * is named (PS-15). `n_lost` users left the entitled group out of `n_group`, and the
 * guard allows at most `threshold` before it asks.
 */
export type PersonalSpacesPurgeBatch = {
  id: number
  n_group: number
  n_lost: number
  threshold: number
  state: PersonalSpacesBatchState
  decided_at: string | null
}

export type PersonalSpacesPurgeBatches = {
  /** False when personal sources are off, unwired, or this FILTH does not serve them. */
  available: boolean
  batches: PersonalSpacesPurgeBatch[]
}

export type PersonalSpacesSourceSettings = {
  feature_id: string
  enabled: boolean
  groups: string[]
  block_groups: string[]
  /** The sliding window of history indexed per user; the admin widens it, never FILTH (PS-28). */
  history_window_days: number
  widen_confirmed_at: string | null
}

export type PersonalSpacesSettings = {
  available: boolean
  sources: Record<PersonalSpacesSource, PersonalSpacesSourceSettings>
  policy?: { policy_version: number; applied_version: number; last_status: string }
}

export async function getPersonalSpacesPurgeBatches(): Promise<PersonalSpacesPurgeBatches> {
  const response = await api.get(`${base}/purge-batches`)
  return (response.data || response) as PersonalSpacesPurgeBatches
}

/** Approve or reject one held batch. The acting admin is the signed-in caller, never a field. */
export async function decidePersonalSpacesPurgeBatch(
  batchId: number,
  decision: "approve" | "reject"
): Promise<{ applied: boolean; batch_id: number; decision: "approved" | "rejected" }> {
  const response = await api.post(`${base}/purge-batches/${batchId}/${decision}`)
  return response.data || response
}

export async function getPersonalSpacesSettings(): Promise<PersonalSpacesSettings> {
  const response = await api.get(`${base}/settings`)
  return (response.data || response) as PersonalSpacesSettings
}

export type PersonalSpacesSourceUpdate = {
  enabled?: boolean
  history_window_days?: number
  /** Required to raise `history_window_days`; the server refuses a widening without it (criterion 20). */
  widen_confirmed?: boolean
}

export async function updatePersonalSpacesSource(
  source: PersonalSpacesSource,
  update: PersonalSpacesSourceUpdate
): Promise<{ success: boolean; message: string; policy_push?: PersonalPolicyPush }> {
  const response = await api.put(`${base}/settings/${source}`, update)
  return response.data || response
}

/**
 * Re-send the policy to FILTH. Without `expectedHidden` it only reports where the policy
 * stands (a held change answers `confirmation_required` again, with the count when FILTH
 * names one). With it, it carries the admin's explicit, audited confirmation of exactly
 * that many hidden users for exactly the policy snapshot `policyDigest` names; FILTH
 * refuses a count that does not match, and a changed policy is not confirmed.
 */
export async function pushPersonalSpacesPolicy(options?: {
  expectedHidden: number
  policyDigest: string
}): Promise<{ policy_push: PersonalPolicyPush }> {
  const body =
    options === undefined
      ? {}
      : {
          confirm: true,
          expected_hidden: options.expectedHidden,
          policy_digest: options.policyDigest,
        }
  const response = await api.post(`${base}/policy/push`, body)
  return response.data || response
}
